import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "./../../components/panel/panel.jsx";

import CategoryModal from "../../components/CustomComponents/modal/parentCategoryModel";
import ModifierSelectionModal from "../../components/CustomComponents/modal/modifierSelectionModal";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Modifier } from "./initializeModifier.js";
import { appSettings } from "../../app/appSettingsSlice";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function UpdateModifierGroup() {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { settingsData, profileData } = useSelector((state) => state.auth);
  const { mGID } = useParams();
  const navigate = useNavigate();
  const [compCurrency, setCompCurrency] = useState("GBP");

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form states with validation
  const [formData, setFormData] = useState({
    name: "",
    subTitle: "",
    type: "List",
    canSkip: true,
    enableQuantity: true,
    minimumSelection: 0,
    maximumSelection: 0,
    freeSelection: 0,
    extraSelectionCharges: 0,
  });

  const [formErrors, setFormErrors] = useState({});
  const [errors, setErrors] = useState({ minimum: '', maximum: '', free: '' });
  const [settings, setSettings] = useState({ s_VAT: "", isTaxEnabled: false });
  const [modifiers, setModifiers] = useState([]);
  const [categories, setCategories] = useState([]);

  // Modal and interaction states
  const [activeRow, setActiveRow] = useState(null);
  const [modals, setModals] = useState({
    showCategoryModal: false,
    showBulkModifierModal: false,
  });

  // Tracking states
  const [addNewRows, setAddNewRows] = useState(new Set());
  const [existingModifierIds, setExistingModifierIds] = useState(new Set());
  const [deletedModifiers, setDeletedModifiers] = useState([]);

  // Refs for managing state
  const initialPricesRef = useRef(new Map());
  const settingsRef = useRef({ s_VAT: "", isTaxEnabled: false });
  const initialFormDataRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const findCategoryById = useCallback((cats, categoryId) => {
    if (!categoryId || !Array.isArray(cats) || cats.length === 0) return null;
    const target = Number(categoryId);
    for (const c of cats) {
      const cId = c?.rootCat_ID ?? c?.rC_ID ?? c?.category_ID ?? c?.id;
      if (cId === target || String(cId) === String(categoryId)) return c;
      const children = c?.subCategoriesList;
      if (Array.isArray(children) && children.length > 0) {
        const found = findCategoryById(children, categoryId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const validModifiersCount = useMemo(() => {
    return modifiers.filter(
      (modifier) => modifier?.menuItem_Name?.trim()
    ).length;
  }, [modifiers]);

  const isExistingModifier = useCallback(
    (modifier) => {
      const id = Number(
        modifier?.menuItem_ID ??
          modifier?.MenuItem_ID ??
          modifier?.menuItemId ??
          modifier?.id
      );
      return (
        id > 0 &&
        (existingModifierIds.has(id) || existingModifierIds.has(String(id)))
      );
    },
    [existingModifierIds]
  );

  const createEmptyModifierRow = useCallback(() => {
    const currentSettings = settingsRef.current;
    const defaultTaxValue =
      currentSettings?.isTaxEnabled && currentSettings?.s_VAT
        ? String(currentSettings.s_VAT)
        : "";

    return {
      ...Modifier,
      menuItem_SalePrice: "",
      menuItem_CostPrice: "",
      menuItem_DeliveryPrice: "",
      menuItem_TakeAwayPrice: "",
      menuItem_TaxPercentage: defaultTaxValue,
      menuItem_IsModifier: true,
      menuItem_Type: "Modifier",
      is_OutOfStock: false,
      menuItem_IsDefaultModifier: false,
      reportCat_ID: "",
      reportCat_Name: "",
      rootCat_ID: "",
      rootCat_Name: "",
    };
  }, []);

  const mapSelectedModifierToRow = useCallback(
    (selectedModifier) => {
      const inferredCatId =
        selectedModifier.rootCat_ID ??
        selectedModifier.category_ID ??
        selectedModifier.catID ??
        "";
      let inferredCatName = "";
      if (inferredCatId && Array.isArray(categories) && categories.length > 0) {
        const found = categories.find(
          (c) =>
            c?.rootCat_ID === inferredCatId ||
            c?.rootCat_ID === Number(inferredCatId)
        );
        inferredCatName = found?.rootCat_Name || "";
      }

      return {
        ...Modifier,
        ...selectedModifier,
        menuItem_IsModifier: true,
        menuItem_Type: "Modifier",
        reportCat_ID:
          inferredCatId ||
          selectedModifier.reportCat_ID ||
          selectedModifier.rootCat_ID ||
          "",
        reportCat_Name:
          inferredCatName ||
          selectedModifier.reportCat_Name ||
          selectedModifier.rootCat_Name ||
          selectedModifier.category_Name ||
          "",
        rootCat_ID:
          inferredCatId ||
          selectedModifier.reportCat_ID ||
          selectedModifier.rootCat_ID ||
          "",
        rootCat_Name:
          inferredCatName ||
          selectedModifier.reportCat_Name ||
          selectedModifier.rootCat_Name ||
          selectedModifier.category_Name ||
          "",
      };
    },
    [categories]
  );

  const addedModifierIds = useMemo(
    () =>
      modifiers
        .map(
          (m) =>
            Number(
              m?.menuItem_ID ?? m?.MenuItem_ID ?? m?.menuItemId ?? m?.id
            ) || 0
        )
        .filter((id) => id > 0),
    [modifiers]
  );

  useEffect(() => {
    const min = parseInt(formData.minimumSelection) || 0;
    const max = parseInt(formData.maximumSelection) || 0;
    const free = parseInt(formData.freeSelection) || 0;
    const num = validModifiersCount;

    let newErrors = { minimum: '', maximum: '', free: '' };

    if (min > max) {
      newErrors.minimum = "Minimum selection cannot be greater than maximum selection";
    }

    let maxErr = '';
    if (max < min) {
      maxErr = "Maximum selection must be greater than or equal to minimum selection";
    }
    if (max > num) {
      const msg = `Maximum selection cannot exceed the number of modifiers (${num})`;
      maxErr = maxErr ? `${maxErr}. ${msg}` : msg;
    }
    newErrors.maximum = maxErr;

    if (free > max) {
      newErrors.free = "Free selection must be less than or equal to maximum selection";
    }

    setErrors(newErrors);
  }, [formData.minimumSelection, formData.maximumSelection, formData.freeSelection, validModifiersCount]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters long";
    } else if (formData.name.trim().length > 100) {
      errors.name = "Name must not exceed 100 characters";
    }

    if (formData.subTitle && formData.subTitle.length > 200) {
      errors.subTitle = "Subtitle must not exceed 200 characters";
    }

    const minSel = parseInt(formData.minimumSelection);
    const maxSel = parseInt(formData.maximumSelection);
    const freeSel = parseInt(formData.freeSelection);

    if (minSel < 0) {
      errors.minimumSelection = "Minimum selection cannot be negative";
    }

    if (maxSel < 0) {
      errors.maximumSelection = "Maximum selection cannot be negative";
    }

    if (freeSel < 0) {
      errors.freeSelection = "Free selection cannot be negative";
    }

    if (parseFloat(formData.extraSelectionCharges) < 0) {
      errors.extraSelectionCharges =
        "Extra selection charges cannot be negative";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const validateModifiers = useCallback(() => {
    const validModifiers = modifiers.filter((m) => m?.menuItem_Name?.trim());

    if (validModifiers.length === 0) {
      showErrorToast("At least one modifier with a name is required");
      return false;
    }

    // Check for duplicate modifier names
    const names = validModifiers.map((m) =>
      m.menuItem_Name?.trim().toLowerCase()
    );
    const duplicates = names.filter(
      (name, index) => names.indexOf(name) !== index
    );

    if (duplicates.length > 0) {
      showErrorToast("Duplicate modifier names are not allowed");
      return false;
    }

    // Validate individual modifier data
    for (let i = 0; i < validModifiers.length; i++) {
      const modifier = validModifiers[i];

      if (!modifier.reportCat_ID && !modifier.rootCat_ID) {
        showErrorToast(
          `Reporting category is required for modifier: ${modifier.menuItem_Name}`
        );
        return false;
      }

      if (
        modifier.menuItem_SalePrice &&
        parseFloat(modifier.menuItem_SalePrice) < 0
      ) {
        showErrorToast(
          `Sale price cannot be negative for modifier: ${modifier.menuItem_Name}`
        );
        return false;
      }

      if (
        modifier.menuItem_CostPrice !== "" &&
        modifier.menuItem_CostPrice != null &&
        parseFloat(modifier.menuItem_CostPrice) < 0
      ) {
        showErrorToast(
          `Cost price cannot be negative for modifier: ${modifier.menuItem_Name}`
        );
        return false;
      }

      if (
        modifier.menuItem_TaxPercentage &&
        (parseFloat(modifier.menuItem_TaxPercentage) < 0 ||
          parseFloat(modifier.menuItem_TaxPercentage) > 100)
      ) {
        showErrorToast(
          `Tax percentage must be between 0 and 100 for modifier: ${modifier.menuItem_Name}`
        );
        return false;
      }

      if (
        modifier.menuItem_DeliveryPrice &&
        parseFloat(modifier.menuItem_DeliveryPrice) < 0
      ) {
        showErrorToast(
          `Delivery price cannot be negative for modifier: ${modifier.menuItem_Name}`
        );
        return false;
      }

      if (
        modifier.menuItem_TakeAwayPrice &&
        parseFloat(modifier.menuItem_TakeAwayPrice) < 0
      ) {
        showErrorToast(
          `Take away price cannot be negative for modifier: ${modifier.menuItem_Name}`
        );
        return false;
      }
    }

    return true;
  }, [modifiers]);

  // Safe value getters with fallbacks
  const safeParseFloat = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "")
      return defaultVal;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const safeParseInt = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "")
      return defaultVal;
    const parsed = parseInt(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const normalizeValue = (value) => {
    return value === null || value === undefined ? "" : String(value);
  };

  // Helper method to get category name from categories list based on category ID
  const getCategoryNameById = useCallback(
    (categoryId) => {
      if (
        !categoryId ||
        !Array.isArray(categories) ||
        categories.length === 0
      ) {
        return "";
      }

      const found = findCategoryById(categories, categoryId);
      return found?.rootCat_Name || found?.rC_Name || "";
    },
    [categories, findCategoryById]
  );

  // Currency management
  const fetchCompCurrency = useCallback(async () => {
    try {
      // Use Redux state instead of API call
      if (!profileData) {
        setCompCurrency("GBP");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, [profileData]);

  // Settings management
  const fetchSettings = useCallback(async () => {
    try {
      // Use Redux state instead of API call
      if (!settingsData) {
        const defaultSettings = { s_VAT: 0, isTaxEnabled: false };
        settingsRef.current = defaultSettings;
        setSettings(defaultSettings);
        return;
      }

      const newSettings = {
        s_VAT: safeParseFloat(settingsData.s_VAT, 0),
        isTaxEnabled: Boolean(settingsData.isTaxEnabled),
      };

      settingsRef.current = newSettings;
      setSettings(newSettings);
    } catch (error) {
      console.error("Failed to load settings:", error);
      showErrorToast("Failed to load tax settings. Using defaults.");
      const defaultSettings = { s_VAT: 0, isTaxEnabled: false };
      settingsRef.current = defaultSettings;
      setSettings(defaultSettings);
    }
  }, [settingsData]);

  // Categories management
  const fetchCategories = useCallback(async (options = {}) => {
    if (!CompID) return [];

    try {
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });

      const categoryData = response.data?.data || response.data;
      const categoriesArray = Array.isArray(categoryData) ? categoryData : [];

      setCategories(categoriesArray);
      return categoriesArray;
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setCategories([]);
      showErrorToast("Failed to load categories");
      return [];
    }
  }, [CompID]);

  // Form data management
  const updateFormData = useCallback((field, value) => {
    let newValue = value;

    // Prevent negative values for numeric fields
    const numValue = parseFloat(value);
    const numericFields = ["minimumSelection", "maximumSelection", "freeSelection", "extraSelectionCharges"];
    if (numericFields.includes(field)) {
      if (!isNaN(numValue) && numValue < 0) {
        newValue = "0";
      }
    }

    setFormData((prev) => ({ ...prev, [field]: newValue }));
    setHasUnsavedChanges(true);
  }, []);

  // Modifier management with comprehensive error handling
  const handleModifierChange = useCallback(
    (index, field, value) => {
      try {
        if (index < 0 || index >= modifiers.length || !field) {
          console.warn("Invalid modifier change parameters:", {
            index,
            field,
            value,
          });
          return;
        }

        const currentModifier = modifiers[index];
        if (!currentModifier) {
          console.warn("Modifier not found at index:", index);
          return;
        }

        const protectedFields = [
          "menuItem_Name",
          "menuItem_SalePrice",
          "menuItem_CostPrice",
          "menuItem_TaxPercentage",
          "menuItem_DeliveryPrice",
          "menuItem_TakeAwayPrice",
          "reportCat_ID",
          "reportCat_Name",
          "rootCat_ID",
          "rootCat_Name",
        ];
        if (isExistingModifier(currentModifier) && protectedFields.includes(field)) {
          return;
        }

        let newValue = value;

        // Validate and clamp numeric fields
        if (
          [
            "menuItem_SalePrice",
            "menuItem_CostPrice",
            "menuItem_DeliveryPrice",
            "menuItem_TakeAwayPrice",
          ].includes(field)
        ) {
          const numValue = parseFloat(value);
          if (value !== "" && (isNaN(numValue) || numValue < 0)) {
            newValue = "0";
          }
        } else if (field === "menuItem_TaxPercentage") {
          const numValue = parseFloat(value);
          if (value !== "" && (isNaN(numValue) || numValue < 0 || numValue > 100)) {
            newValue = Math.max(0, Math.min(100, numValue)).toString();
          }
        }

        const updatedModifiers = modifiers.map((modifier, i) =>
          i === index ? { ...modifier, [field]: newValue } : modifier
        );

        // Check if we need to add a new row
        const shouldAddNewRow =
          index === modifiers.length - 1 &&
          ((field === "menuItem_Name" && newValue?.trim()) ||
            (field === "menuItem_SalePrice" &&
              newValue &&
              parseFloat(newValue) > 0) ||
            (field === "menuItem_DeliveryPrice" &&
              newValue &&
              parseFloat(newValue) > 0) ||
            (field === "menuItem_TakeAwayPrice" &&
              newValue &&
              parseFloat(newValue) > 0) ||
            (field === "menuItem_TaxPercentage" &&
              newValue &&
              parseFloat(newValue) > 0) ||
            (field === "menuItem_CostPrice" &&
              newValue &&
              parseFloat(newValue) > 0) ||
            (field === "reportCat_ID" && newValue));

        if (shouldAddNewRow) {
          setModifiers([...updatedModifiers, createEmptyModifierRow()]);
        } else {
          setModifiers(updatedModifiers);
        }

        setHasUnsavedChanges(true);
      } catch (error) {
        console.error("Error updating modifier:", error);
        showErrorToast("Failed to update modifier");
      }
    },
    [modifiers, isExistingModifier, createEmptyModifierRow]
  );

  const handleRemoveModifier = useCallback(
    (index) => {
      try {
        if (modifiers.length === 1) {
          showErrorToast("At least one modifier is required");
          return;
        }

        if (index < 0 || index >= modifiers.length) {
          showErrorToast("Invalid modifier index");
          return;
        }

        const modifierToRemove = modifiers[index];
        const existingId =
          modifierToRemove?.menuItem_ID ??
          modifierToRemove?.MenuItem_ID ??
          modifierToRemove?.menuItemId ??
          modifierToRemove?.id;

        if (existingId) {
          setDeletedModifiers((prev) => [...prev, existingId]);
          const removedId = Number(existingId);
          setExistingModifierIds((prev) => {
            const next = new Set(prev);
            next.delete(removedId);
            next.delete(String(removedId));
            return next;
          });
        }

        setAddNewRows((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });

        const updatedModifiers = modifiers.filter((_, i) => i !== index);
        setModifiers(updatedModifiers);
        setHasUnsavedChanges(true);
      } catch (error) {
        console.error("Error removing modifier:", error);
        showErrorToast("Failed to remove modifier");
      }
    },
    [modifiers]
  );

  const handleMultiModifierSelect = useCallback(
    (selectedModifiers) => {
      try {
        if (!Array.isArray(selectedModifiers) || selectedModifiers.length === 0) {
          return;
        }

        const newModifierIds = [];

        setModifiers((prevModifiers) => {
          const idsInTable = new Set(
            prevModifiers
              .map(
                (m) =>
                  Number(
                    m?.menuItem_ID ?? m?.MenuItem_ID ?? m?.menuItemId ?? m?.id
                  ) || 0
              )
              .filter((id) => id > 0)
          );

          let base = [...prevModifiers];
          const lastRow = base[base.length - 1];
          const lastRowIsEmpty =
            !lastRow?.menuItem_Name?.trim() &&
            !Number(
              lastRow?.menuItem_ID ??
                lastRow?.MenuItem_ID ??
                lastRow?.menuItemId ??
                lastRow?.id
            );
          if (lastRowIsEmpty && base.length > 0) {
            base = base.slice(0, -1);
          }

          const rowsToAdd = selectedModifiers
            .filter(
              (m) =>
                m?.menuItem_ID && !idsInTable.has(Number(m.menuItem_ID))
            )
            .map((m) => {
              newModifierIds.push(m.menuItem_ID);
              const row = mapSelectedModifierToRow(m);
              const id = Number(m.menuItem_ID);
              const key = String(id);
              initialPricesRef.current.set(key, {
                sale: normalizeValue(row.menuItem_SalePrice),
                cost: normalizeValue(row.menuItem_CostPrice),
                tax: normalizeValue(row.menuItem_TaxPercentage),
                del: normalizeValue(row.menuItem_DeliveryPrice),
                take: normalizeValue(row.menuItem_TakeAwayPrice),
              });
              return row;
            });

          return [...base, ...rowsToAdd, createEmptyModifierRow()];
        });

        if (newModifierIds.length > 0) {
          setExistingModifierIds(
            (prev) =>
              new Set([
                ...prev,
                ...newModifierIds.map((id) => Number(id)),
              ])
          );
          showSuccessToast(
            `${newModifierIds.length} existing modifier(s) added`
          );
          setHasUnsavedChanges(true);
        }
      } catch (error) {
        console.error("Error selecting modifiers:", error);
        showErrorToast("Failed to add modifiers");
      }
    },
    [createEmptyModifierRow, mapSelectedModifierToRow]
  );

  const handleNewModifierNameChange = useCallback(
    (index, value) => {
      if (isExistingModifier(modifiers[index])) {
        return;
      }
      setAddNewRows((prev) => new Set([...prev, index]));
      handleModifierChange(index, "menuItem_Name", value);
    },
    [modifiers, isExistingModifier, handleModifierChange]
  );

  // Drag and drop with error handling
  const handleDragStart = (e, index) => {
    try {
      dragItem.current = index;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/json", JSON.stringify(index));
      e.currentTarget.style.opacity = "0.5";
    } catch (error) {
      console.error("Error starting drag:", error);
    }
  };

  const handleDragEnd = (e) => {
    try {
      e.currentTarget.style.opacity = "1";
      document.querySelectorAll("tr").forEach((tr) => {
        tr.style.backgroundColor = "";
      });
      dragItem.current = null;
      dragOverItem.current = null;
    } catch (error) {
      console.error("Error ending drag:", error);
    }
  };

  const handleDrop = (e) => {
    try {
      e.preventDefault();
      const draggedIndex = JSON.parse(
        e.dataTransfer.getData("application/json")
      );
      const overIndex = dragOverItem.current;

      if (
        draggedIndex === overIndex ||
        draggedIndex === undefined ||
        overIndex === undefined
      ) {
        return;
      }

      if (
        draggedIndex < 0 ||
        draggedIndex >= modifiers.length ||
        overIndex < 0 ||
        overIndex >= modifiers.length
      ) {
        showErrorToast("Invalid drag operation");
        return;
      }

      const newModifiers = [...modifiers];
      const draggedModifier = newModifiers[draggedIndex];
      newModifiers.splice(draggedIndex, 1);
      newModifiers.splice(overIndex, 0, draggedModifier);

      setModifiers(newModifiers);
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error("Error handling drop:", error);
      showErrorToast("Failed to reorder modifiers");
    } finally {
      dragItem.current = null;
      dragOverItem.current = null;
    }
  };

  // Data fetching with comprehensive error handling
  const fetchModifierGroupData = useCallback(
    async (categoriesList = []) => {
      try {
        if (!CompID || !mGID) {
          throw new Error("Missing required parameters");
        }

        setInitLoading(true);
        const response = await axiosInstance.get(
          APIs.GET_MODIFIER_GROUP_BY_ID,
          {
            params: { CompID, ModifierGroupID: mGID },
          }
        );

        const data = response.data?.data || response.data;
        if (!data) {
          throw new Error("No modifier group data received");
        }

        const newFormData = {
          name: data.name || "",
          subTitle: data.subTitle || "",
          type: data.type || "List",
          canSkip: Boolean(data.canSkip ?? true),
          enableQuantity: Boolean(data.enableQuantity ?? true),
          minimumSelection: safeParseInt(data.minimumSelection),
          maximumSelection: safeParseInt(data.maximumSelection),
          freeSelection: safeParseInt(data.freeSelection),
          extraSelectionCharges: safeParseFloat(data.extraSelectionCharges),
        };

        setFormData(newFormData);
        initialFormDataRef.current = { ...newFormData };

        const getCategoryNameFromList = (categoryId) => {
          if (
            !categoryId ||
            !Array.isArray(categoriesList) ||
            categoriesList.length === 0
          ) {
            return "";
          }

          const found = findCategoryById(categoriesList, categoryId);
          return found?.rootCat_Name || found?.rC_Name || "";
        };

        let validModifiers = [];
        if (Array.isArray(data.modifiers)) {
          validModifiers = data.modifiers
            .filter((m) => m && typeof m === "object")
            .map((m) => {
              const categoryId = m.reportCat_ID || m.rootCat_ID || "";
              const lookedUpName = categoryId ? getCategoryNameFromList(categoryId) : "";
              // If categories haven't loaded yet, fall back to API-provided name
              const categoryName =
                lookedUpName ||
                m.reportCat_Name ||
                m.rootCat_Name ||
                "";

              return {
                ...m,
                reportCat_ID: categoryId,
                reportCat_Name: categoryName,
                rootCat_ID: categoryId,
                rootCat_Name: categoryName,
                menuItem_SalePrice: m.menuItem_SalePrice || "",
                menuItem_CostPrice: m.menuItem_CostPrice || "",
                menuItem_DeliveryPrice: m.menuItem_DeliveryPrice || "",
                menuItem_TakeAwayPrice: m.menuItem_TakeAwayPrice || "",
                menuItem_TaxPercentage: m.menuItem_TaxPercentage || "",
                is_OutOfStock: Boolean(m.is_OutOfStock),
                menuItem_IsDefaultModifier: Boolean(
                  m.menuItem_IsDefaultModifier
                ),
              };
            });
        }

        const currentSettings = settingsRef.current;
        const defaultTaxValue =
          currentSettings?.isTaxEnabled && currentSettings?.s_VAT
            ? String(currentSettings.s_VAT)
            : "";

        const newModifier = {
          ...Modifier,
          menuItem_SalePrice: "",
          menuItem_CostPrice: "",
          menuItem_DeliveryPrice: "",
          menuItem_TakeAwayPrice: "",
          menuItem_TaxPercentage: defaultTaxValue,
          menuItem_IsModifier: true,
          menuItem_Type: "Modifier",
          is_OutOfStock: false,
          menuItem_IsDefaultModifier: false,
          reportCat_ID: "", // Explicitly clear
          reportCat_Name: "", // Explicitly clear
          rootCat_ID: "", // Explicitly clear
          rootCat_Name: "", // Explicitly clear
        };

        setModifiers([...validModifiers, newModifier]);

        const ids = new Set();
        const priceMap = new Map();

        validModifiers.forEach((m) => {
          const id = m?.menuItem_ID ?? m?.MenuItem_ID ?? m?.menuItemId ?? m?.id;
          if (id) {
            const numId = Number(id);
            const key = String(id);
            ids.add(numId);
            priceMap.set(key, {
              sale: normalizeValue(m.menuItem_SalePrice),
              cost: normalizeValue(m.menuItem_CostPrice),
              tax: normalizeValue(m.menuItem_TaxPercentage),
              del: normalizeValue(m.menuItem_DeliveryPrice),
              take: normalizeValue(m.menuItem_TakeAwayPrice),
            });
          }
        });

        setExistingModifierIds(ids);
        initialPricesRef.current = priceMap;
      } catch (error) {
        console.error("Error fetching modifier group data:", error);
        showErrorToast("Failed to load modifier group data");
        navigate("/modifiers/manageModifierGroups");
      } finally {
        setInitLoading(false);
      }
    },
    [mGID, CompID, navigate]
  );

  // Submission with comprehensive validation and error handling
  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    try {
      if (!validateForm() || !validateModifiers()) {
        return false;
      }

      const hasErrors = Object.values(errors).some(e => e);
      if (hasErrors) {
        showErrorToast("Please correct the validation errors");
        return false;
      }

      setLoading(true);

      const validModifiers = modifiers
        .filter((modifier) => modifier?.menuItem_Name?.trim())
        .map((item) => {
          const sale = safeParseFloat(item.menuItem_SalePrice);
          const delivery =
            item.menuItem_DeliveryPrice === undefined ||
            item.menuItem_DeliveryPrice === null ||
            item.menuItem_DeliveryPrice === ""
              ? sale
              : safeParseFloat(item.menuItem_DeliveryPrice);
          const takeAway =
            item.menuItem_TakeAwayPrice === undefined ||
            item.menuItem_TakeAwayPrice === null ||
            item.menuItem_TakeAwayPrice === ""
              ? sale
              : safeParseFloat(item.menuItem_TakeAwayPrice);

          const reportCatId = item.reportCat_ID || item.rootCat_ID || "";
          const reportCatName = item.reportCat_Name || item.rootCat_Name || "";

          return {
            menuItem_ID: item.menuItem_ID || 0,
            menuItem_Name: item.menuItem_Name || "",
            allergens: item.allergens || "",
            menuItem_IsModifier: true,
            menuItem_Type: "Modifier",
            compID: CompID,
            itsMasterProductID: safeParseInt(item.itsMasterProductID),
            MenuItem_AETime: item.MenuItem_AETime || "",
            MenuItem_ASTime: item.MenuItem_ASTime || "",
            MenuItem_BarCode: item.MenuItem_BarCode || "",
            MenuItem_Detail: item.MenuItem_Detail || "",
            MenuItem_Image: item.MenuItem_Image || "",
            MenuItem_ImageAltText: item.MenuItem_ImageAltText || "",
            MenuItem_KitchenDisplayName: item.MenuItem_KitchenDisplayName || "",
            MenuItem_Permalink: item.MenuItem_Permalink || "",
            MenuItem_PreparationTime: item.MenuItem_PreparationTime || "",
            MenuItem_RootType: item.MenuItem_RootType || "",
            MenuItem_SEODescription: item.MenuItem_SEODescription || "",
            MenuItem_SEOTitle: item.MenuItem_SEOTitle || "",
            MenuItem_SKU: item.MenuItem_SKU || "",
            MenuItem_ServingSize: item.MenuItem_ServingSize || "",
            menuItem_DeliveryPrice: delivery,
            menuItem_TakeAwayPrice: takeAway,
            menuItem_SalePrice: sale,
            menuItem_CostPrice: safeParseFloat(item.menuItem_CostPrice),
            menuItem_TaxPercentage: safeParseFloat(item.menuItem_TaxPercentage),
            reportCat_ID: reportCatId,
            reportCat_Name: reportCatName,
            is_OutOfStock: Boolean(item.is_OutOfStock),
            menuItem_IsDefaultModifier: Boolean(
              item.menuItem_IsDefaultModifier
            ),
          };
        });

      if (validModifiers.length === 0) {
        showErrorToast("At least one modifier is required");
        return false;
      }

      const payload = {
        CompID,
        modifierGroupID: mGID,
        ...formData,
        minimumSelection: safeParseInt(formData.minimumSelection),
        maximumSelection: safeParseInt(formData.maximumSelection),
        freeSelection: safeParseInt(formData.freeSelection),
        extraSelectionCharges: safeParseFloat(formData.extraSelectionCharges),
        detail: "",
        updateDatetime: new Date().toISOString(),
        Modifiers: validModifiers,
      };

      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_MODIFIER_GROUP,
        payload
      );

      if (response.data.status) {
        showSuccessToast("Modifier Group updated successfully");
        setHasUnsavedChanges(false);
        if (exitAfterSave) {
          navigate("/modifiers/manageModifierGroups");
        }
        return true;
      } else {
        throw new Error(
          response.data?.message || "Failed to update Modifier Group"
        );
      }
    } catch (error) {
      console.error("Error updating modifier group:", error);
      showErrorToast(
        error.response?.data?.message ||
          error.message ||
          "An error occurred while updating the Modifier Group"
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty: hasUnsavedChanges,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => setHasUnsavedChanges(false),
  });

  // Initialize settings effect
  useEffect(() => {
    if (!settings.s_VAT && !settings.isTaxEnabled) return;

    settingsRef.current = settings;

    if (settings.isTaxEnabled && settings.s_VAT) {
      setModifiers((prevModifiers) =>
        prevModifiers.map((modifier) => {
          const isNewModifier =
            !modifier.menuItem_Name?.trim() &&
            !modifier.menuItem_SalePrice &&
            !modifier.reportCat_ID &&
            (!modifier.menuItem_TaxPercentage ||
              modifier.menuItem_TaxPercentage === 0 ||
              modifier.menuItem_TaxPercentage === "0" ||
              modifier.menuItem_TaxPercentage === "");

          return isNewModifier
            ? {
                ...modifier,
                menuItem_TaxPercentage: String(settings.s_VAT),
              }
            : modifier;
        })
      );
    }
  }, [settings.s_VAT, settings.isTaxEnabled]);

  // Category name backfilling effect
  useEffect(() => {
    if (!Array.isArray(categories) || categories.length === 0) return;
    if (!Array.isArray(modifiers) || modifiers.length === 0) return;

    setModifiers((prevModifiers) => {
      let hasChanges = false;
      const updatedModifiers = prevModifiers.map((m, index) => {
        let updatedModifier = { ...m };

        const categoryId = m.reportCat_ID || m.rootCat_ID;
        const categoryName = m.reportCat_Name || m.rootCat_Name;

        if (categoryId && (!categoryName || categoryName.trim() === "") && m.menuItem_Name) {
          const foundName = getCategoryNameById(categoryId);
          if (foundName) {
            hasChanges = true;
            updatedModifier.reportCat_Name = foundName;
            updatedModifier.rootCat_Name = foundName;
          }
        }

        return updatedModifier;
      });

      return hasChanges ? updatedModifiers : prevModifiers;
    });
  }, [categories, modifiers, getCategoryNameById]);

  // Initial data loading
  useEffect(() => {
    if (!CompID || !mGID) {
      showErrorToast("Missing required information");
      navigate("/modifiers/manageModifierGroups");
      return;
    }

    const initializeData = async () => {
      try {
        await fetchCompCurrency();
        await fetchSettings();
        const categoriesData = await fetchCategories();
        await fetchModifierGroupData(categoriesData);
      } catch (error) {
        console.error("Error initializing data:", error);
        showErrorToast("Failed to initialize data");
      }
    };

    initializeData();
  }, [
    CompID,
    mGID,
    fetchCompCurrency,
    fetchModifierGroupData,
    fetchCategories,
    fetchSettings,
    navigate,
  ]);

  // Error boundary for ResizeObserver
  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener("error", handleResizeObserverError);
    return () => window.removeEventListener("error", handleResizeObserverError);
  }, []);

  // Handle category selection with error handling
  const handleCategorySelect = useCallback(
    (categoryData) => {
      try {
        if (activeRow === null || !categoryData) {
          showErrorToast("Invalid category selection");
          return;
        }

        const pickedId = categoryData.rootCat_ID || "";
        const pickedName = categoryData.rootCat_Name || "";

        if (
          activeRow >= 0 &&
          activeRow < modifiers.length &&
          !isExistingModifier(modifiers[activeRow])
        ) {
          setModifiers((prevModifiers) =>
            prevModifiers.map((modifier, index) =>
              index === activeRow
                ? {
                    ...modifier,
                    reportCat_ID: pickedId,
                    reportCat_Name: pickedName || "",
                    rootCat_ID: pickedId,
                    rootCat_Name: pickedName || "",
                  }
                : modifier
            )
          );
          setHasUnsavedChanges(true);
        }

        setModals((prev) => ({ ...prev, showCategoryModal: false }));
      } catch (error) {
        console.error("Error selecting category:", error);
        showErrorToast("Failed to select category");
      }
    },
    [activeRow, modifiers, isExistingModifier]
  );

  // Loading screen for initial load
  if (initLoading) {
    return (<GridSectionLoader loading={true} minHeight="70vh" />);
  }

  // Error boundary fallback
  if (!CompID || !mGID) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "50vh" }}
      >
        <div className="text-center">
          <h3>Error Loading Modifier Group</h3>
          <p>Missing required parameters. Please try again.</p>
          <Link
            to="/modifiers/manageModifierGroups"
            className="btn btn-primary"
          >
            Back to Modifier Groups
          </Link>
        </div>
      </div>
    );
  }

  const hasSelectionErrors = Object.values(errors).some(e => e);

  return (
    <div>
      

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/modifiers/manageModifierGroups">
                Manage Modifier Groups
              </Link>
            </li>
            <li className="breadcrumb-item active">Update Modifier Group</li>
          </ol>
          <h1 className="page-header mb-0">Update Modifier Group</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/modifiers/manageModifierGroups")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
          disabled={!formData.name?.trim() || hasSelectionErrors}
          savingLabel="Updating..."
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Modifier Group Details</PanelHeader>
          <PanelBody>
            <div
              className="p-4 rounded-3 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                border: isDarkMode ? "1px solid #404040" : undefined,
                color: isDarkMode ? "#ffffff" : undefined,
              }}
            >
              <div className="row mb-4">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Name *"
                      placeHolder="Enter Modifier Group Name"
                      type="text"
                      value={formData.name}
                      onchange={(e) => updateFormData("name", e.target.value)}
                      error={formErrors.name}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Subtitle"
                      placeHolder="Enter Subtitle"
                      type="text"
                      value={formData.subTitle}
                      onchange={(e) =>
                        updateFormData("subTitle", e.target.value)
                      }
                      error={formErrors.subTitle}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Minimum Selection"
                      placeHolder="Enter Minimum Selection"
                      type="number"
                      min="0"
                      value={formData.minimumSelection}
                      onchange={(e) =>
                        updateFormData("minimumSelection", e.target.value)
                      }
                      error={formErrors.minimumSelection}
                    />
                    {errors.minimum && <small className="text-danger d-block">{errors.minimum}</small>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Maximum Selection"
                      placeHolder="Enter Maximum Selection"
                      type="number"
                      min="0"
                      value={formData.maximumSelection}
                      onchange={(e) =>
                        updateFormData("maximumSelection", e.target.value)
                      }
                      error={formErrors.maximumSelection}
                    />
                    {errors.maximum && <small className="text-danger d-block">{errors.maximum}</small>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Free Selection"
                      placeHolder="Enter Free Selection Count"
                      type="number"
                      min="0"
                      value={formData.freeSelection}
                      onchange={(e) =>
                        updateFormData("freeSelection", e.target.value)
                      }
                      error={formErrors.freeSelection}
                    />
                    {errors.free && <small className="text-danger d-block">{errors.free}</small>}
                  </div>
                  <div className="col-md-6 mb-3">
                    
                  </div>
                  <div className="col-md-6 mb-3 d-none">
                    <FormInput
                      label="Extra Selection Charges"
                      placeHolder="Enter Extra Charges"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.extraSelectionCharges}
                      onchange={(e) =>
                        updateFormData("extraSelectionCharges", e.target.value)
                      }
                      error={formErrors.extraSelectionCharges}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={formData.canSkip}
                      onToggle={() =>
                        updateFormData("canSkip", !formData.canSkip)
                      }
                      label="Can Skip"
                      disabled={loading}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={formData.enableQuantity}
                      onToggle={() =>
                        updateFormData("enableQuantity", !formData.enableQuantity)
                      }
                      label="Enable Quantity"
                      disabled={loading}
                      themeColor={themeColor}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h5 className="mb-0">Modifiers</h5>
                    <div className="d-flex align-items-center gap-3">
                      {hasUnsavedChanges && (
                        <small className="text-warning mb-0">
                          <i className="fa fa-exclamation-triangle me-1"></i>
                          You have unsaved changes
                        </small>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm px-3"
                        onClick={() =>
                          setModals((prev) => ({
                            ...prev,
                            showBulkModifierModal: true,
                          }))
                        }
                        disabled={loading}
                        style={{
                          backgroundColor: themeColor,
                          border: `1px solid ${themeColor}`,
                          color: "#fff",
                          borderRadius: "8px",
                          fontWeight: 600,
                        }}
                      >
                        <i className="fa fa-list me-2"></i>
                        Select Existing Modifiers
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table">
                      <thead
                        style={{
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#212529",
                        }}
                      >
                        <tr>
                          <th>Modifier *</th>
                          <th>
                            Cost Price ({getCurrencySymbol(compCurrency)})
                          </th>
                          <th>Tax %</th>
                          <th>
                            Sale Price ({getCurrencySymbol(compCurrency)})
                          </th>
                          <th>
                            Delivery Price ({getCurrencySymbol(compCurrency)})
                          </th>
                          <th>
                            Take Away Price ({getCurrencySymbol(compCurrency)})
                          </th>
                          <th>Category *</th>
                          <th>Stock Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modifiers
                          .filter(
                            (modifier) =>
                              modifier && typeof modifier === "object"
                          )
                          .map((modifier, index) => {
                            const rowIsExisting = isExistingModifier(modifier);
                            return (
                            <React.Fragment
                              key={`modifier-${index}-${
                                modifier?.menuItem_ID || "new"
                              }`}
                            >
                              <tr
                                draggable={!loading}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnter={(e) => {
                                  e.preventDefault();
                                  dragOverItem.current = index;
                                  e.currentTarget.style.backgroundColor =
                                    isDarkMode ? "#343a40" : "#e9ecef";
                                }}
                                onDragLeave={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.style.backgroundColor = "";
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDrop}
                                onDragEnd={handleDragEnd}
                                style={{
                                  opacity:
                                    dragItem.current === index ? "0.5" : "1",
                                }}
                              >
                                <td className="align-middle">
                                  <div className="d-flex align-items-center">
                                    <FormInput
                                      type="text"
                                      value={modifier.menuItem_Name || ""}
                                      placeHolder={
                                        rowIsExisting
                                          ? "Existing modifier"
                                          : "Type new modifier name"
                                      }
                                      readOnly={rowIsExisting}
                                      onchange={(e) =>
                                        handleNewModifierNameChange(
                                          index,
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        cursor: rowIsExisting
                                          ? "not-allowed"
                                          : "text",
                                        backgroundColor: rowIsExisting
                                          ? (isDarkMode ? "#343a40" : "#F0F0F0")
                                          : (isDarkMode ? "#2d3436" : "#FFFFFF"),
                                      }}
                                      disabled={loading}
                                    />
                                    {rowIsExisting && (
                                      <i
                                        className="fa fa-lock text-muted ms-2"
                                        title="Existing modifier — use Select Existing Modifiers to add. Price and details cannot be edited here."
                                        style={{ fontSize: "16px" }}
                                      ></i>
                                    )}
                                  </div>
                                </td>
                                <td className="align-middle">
                                  <FormInput
                                    type="number"
                                    value={modifier.menuItem_CostPrice ?? ""}
                                    placeHolder="0.00"
                                    step="0.01"
                                    min="0"
                                    readOnly={rowIsExisting}
                                    onchange={(e) =>
                                      handleModifierChange(
                                        index,
                                        "menuItem_CostPrice",
                                        e.target.value
                                      )
                                    }
                                    disabled={loading}
                                  />
                                </td>
                                <td className="align-middle">
                                  <FormInput
                                    type="number"
                                    value={
                                      modifier.menuItem_TaxPercentage ?? ""
                                    }
                                    placeHolder="0.00"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    readOnly={rowIsExisting}
                                    onchange={(e) =>
                                      handleModifierChange(
                                        index,
                                        "menuItem_TaxPercentage",
                                        e.target.value
                                      )
                                    }
                                    disabled={loading}
                                  />
                                </td>
                                <td className="align-middle">
                                  <FormInput
                                    type="number"
                                    value={modifier.menuItem_SalePrice ?? ""}
                                    placeHolder="0.00"
                                    step="0.01"
                                    min="0"
                                    readOnly={rowIsExisting}
                                    onchange={(e) =>
                                      handleModifierChange(
                                        index,
                                        "menuItem_SalePrice",
                                        e.target.value
                                      )
                                    }
                                    disabled={loading}
                                  />
                                </td>
                                <td className="align-middle">
                                  <FormInput
                                    type="number"
                                    value={
                                      modifier.menuItem_DeliveryPrice ?? ""
                                    }
                                    placeHolder="0.00 (Same as sale price)"
                                    step="0.01"
                                    min="0"
                                    readOnly={rowIsExisting}
                                    onchange={(e) =>
                                      handleModifierChange(
                                        index,
                                        "menuItem_DeliveryPrice",
                                        e.target.value
                                      )
                                    }
                                    disabled={loading}
                                  />
                                </td>
                                <td className="align-middle">
                                  <FormInput
                                    type="number"
                                    value={
                                      modifier.menuItem_TakeAwayPrice ?? ""
                                    }
                                    placeHolder="0.00 (Same as sale price)"
                                    step="0.01"
                                    min="0"
                                    readOnly={rowIsExisting}
                                    onchange={(e) =>
                                      handleModifierChange(
                                        index,
                                        "menuItem_TakeAwayPrice",
                                        e.target.value
                                      )
                                    }
                                    disabled={loading}
                                  />
                                </td>
                                <td className="align-middle">
                                  <div className="position-relative">
                                    <FormInput
                                      label=""
                                      placeHolder="Select Category"
                                      type="text"
                                      value={
                                        modifier.reportCat_Name ||
                                        modifier.rootCat_Name ||
                                        ""
                                      }
                                      onChange={() => {}}
                                      onclick={
                                        rowIsExisting
                                          ? undefined
                                          : () => {
                                              setActiveRow(index);
                                              setModals((prev) => ({
                                                ...prev,
                                                showCategoryModal: true,
                                              }));
                                            }
                                      }
                                      readOnly
                                      icon={
                                        rowIsExisting ? "" : "fa fa-caret-down"
                                      }
                                      iconOnClick={
                                        rowIsExisting
                                          ? undefined
                                          : () => {
                                              setActiveRow(index);
                                              setModals((prev) => ({
                                                ...prev,
                                                showCategoryModal: true,
                                              }));
                                            }
                                      }
                                      secIcon={
                                        !rowIsExisting &&
                                        (modifier.reportCat_ID ||
                                          modifier.rootCat_ID)
                                          ? "fa fa-times-circle"
                                          : ""
                                      }
                                      secIconOnClick={
                                        rowIsExisting
                                          ? undefined
                                          : () => {
                                              setModifiers((prevModifiers) =>
                                                prevModifiers.map((mod, i) =>
                                                  i === index
                                                    ? {
                                                        ...mod,
                                                        reportCat_ID: "",
                                                        reportCat_Name: "",
                                                        rootCat_ID: "",
                                                        rootCat_Name: "",
                                                      }
                                                    : mod
                                                )
                                              );
                                              setHasUnsavedChanges(true);
                                            }
                                      }
                                      className="form-control"
                                      name="category"
                                      disabled={loading}
                                    />
                                  </div>
                                </td>
                                <td className="align-middle">
                                  <div
                                    className="d-flex flex-column gap-2 p-2"
                                    style={{ minWidth: "120px" }}
                                  >
                                    <ToggleSwitch
                                      isChecked={!modifier.is_OutOfStock}
                                      onToggle={() =>
                                        handleModifierChange(
                                          index,
                                          "is_OutOfStock",
                                          !modifier.is_OutOfStock
                                        )
                                      }
                                      label="In stock"
                                      disabled={false}
                                      themeColor={themeColor}
                                    />
                                    <ToggleSwitch
                                      isChecked={
                                        modifier.menuItem_IsDefaultModifier
                                      }
                                      label="Default"
                                      onToggle={() =>
                                        handleModifierChange(
                                          index,
                                          "menuItem_IsDefaultModifier",
                                          !modifier.menuItem_IsDefaultModifier
                                        )
                                      }
                                      themeColor={themeColor}
                                    />
                                  </div>
                                </td>
                                <td className="align-middle">
                                  <div className="d-flex align-items-center m-1">
                                    <button
                                      type="button"
                                      className="btn btn-link text-danger p-1"
                                      onClick={() =>
                                        handleRemoveModifier(index)
                                      }
                                      disabled={
                                        loading || modifiers.length === 1
                                      }
                                      title={
                                        modifiers.length === 1
                                          ? "At least one modifier is required"
                                          : "Remove modifier"
                                      }
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                    <i
                                      className="fa fa-bars ms-2"
                                      style={{
                                        cursor: loading
                                          ? "not-allowed"
                                          : "grab",
                                      }}
                                      title="Drag to reorder"
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            </React.Fragment>
                          );})}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>

      <CategoryModal
        key={`category-modal-${activeRow}`}
        showParentCategoryModal={modals.showCategoryModal}
        setShowParentCategoryModal={(val) =>
          setModals((prev) => ({ ...prev, showCategoryModal: val }))
        }
        setSelectedParentCategory={handleCategorySelect}
        onSubmit={handleCategorySelect}
        compID={CompID}
        selectedParentCategory={
          activeRow !== null && modifiers[activeRow]?.reportCat_ID
            ? {
                rootCat_ID: modifiers[activeRow].reportCat_ID,
                rootCat_Name: modifiers[activeRow].reportCat_Name,
              }
            : null
        }
        categories={categories}
        showButton={true}
        isMultiSelect={false}
        modalTitle="Select Category"
        modalSubTitle="Select a category for the modifier"
        fetchCategories={fetchCategories}
      />

      <ModifierSelectionModal
        showModal={modals.showBulkModifierModal}
        setShowModal={(val) =>
          setModals((prev) => ({
            ...prev,
            showBulkModifierModal: val,
          }))
        }
        isMultiSelect={true}
        onMultiSelect={handleMultiModifierSelect}
        showAddNewButton={false}
        excludeModifierIds={addedModifierIds}
        compID={CompID}
        modalTitle="Select Existing Modifiers"
        modalSubTitle="Choose one or more modifiers already in your library"
        confirmButtonLabel="Add to Group"
      />
    </div>
  );
}

export default UpdateModifierGroup;